<!--
 Name: App.vue
 Description: Base component.
 Created by: Jana Schumann on 2019-11-05
 Last edited by: Andjelka Popovic on 2025-12-22
-->

<template>
  <v-app id="app-root">

    <!-- side menu shown only on small devices -->
    <app-side-menu
      class="hidden-md-and-up"
      v-model:openSideMenu="openSideMenu"
      :mainMenu="mainMenu"
    />

    <!-- header (with the menu shown on large devices) -->
    <app-header
      v-model:openSideMenu="openSideMenu"
      :mainMenu="mainMenu"
    />

    <!-- page content -->
    <v-main class="bg-white">
      <router-view/>
    </v-main>

    <!-- footer -->
    <app-footer
      :mainMenu="mainMenu"
    />

    <cookie-banner :key="$route.path"/>
  </v-app>
</template>

<script>
import Footer from "./components/Footer.vue";
import Header from "./components/Header.vue";
import SideMenu from "./components/SideMenu.vue";
import CookieBanner from "./components/CookieBanner.vue";

export default {
  name: "App",
  components: {
    appFooter: Footer,
    appHeader: Header,
    appSideMenu: SideMenu,
    CookieBanner
  },
  data() {
    return {
      openSideMenu: false,
      languageSwitch: null,
      zammadChatInitialized: false
    }
  },
  computed: {
    // computed to make the locale dynamic
    mainMenu() {
      return [
        {
          headline: "footer.neotiv.headline",
          items: [
            {title: "footer.neotiv.team", routeName: "Team"},
            {title: "footer.neotiv.partnerships", routeName: "Partnerships"},
            {title: "footer.neotiv.press", routeName: "Press"},
            {title: "footer.neotiv.certificates", routeName: "Certificates"},
            {title: "footer.neotiv.jobs", link: `https://neotiv.jobs.personio.de/?language=${this.$i18n.locale}`}
          ]
        },
        {
          headline: "footer.science.headline",
          items: [
            {title: "footer.science.background", routeName: "Background"},
            {title: "footer.science.hiw", routeName: "HowItWorks"},
            {title: "footer.science.validation", routeName: "ValidationStrategy"},
            {title: "footer.science.studies", routeName: "Collaborations & Studies"},
            {title: "footer.science.papers", routeName: "Publications"}
          ]
        },
        {
          headline: "footer.professionals.headline",
          items: [
            {title: "footer.professionals.scientists", routeName: "Scientists"},
            {title: "footer.professionals.researchers", routeName: "Researchers"},
            {title: "footer.professionals.physicians", link: `https://neotiv-care.com/medizinisches-fachpersonal`}
          ]
        }
      ]
    }
  },
  mounted() {
    const loadZammadChat = () => {
      const zammadChatScript = document.createElement('script')
      zammadChatScript.src = 'https://support.neotiv.com/assets/chat/chat-no-jquery.min.js'
      zammadChatScript.onload = () => {
        new ZammadChat({
          title: this.$i18n.locale === 'de' ? 'Chatten Sie mit uns!' : 'Chat',
          background: 'rgb(87, 80, 156)',
          fontSize: '16px',
          flat: true,
          chatId: 4,
          show: true
        })
      }
      document.body.appendChild(zammadChatScript)
    }
    loadZammadChat()
  }
}
</script>

<style>
/* we need to refactor these styles at some point and introduce sass variables */

/* Hind regular */



body {
  font-size: 22px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  line-height: 1.45;
}

.v-application {
  font-family: "Source Sans 3", sans-serif !important;
}

/* App layout */
#app-root {
  background-color: #efefef;
}
/* vuetify default app structure doesn't support max-width, centered layouts */
/* https://github.com/vuetifyjs/vuetify/issues/4267 */
.v-application__wrap {
  max-width: 1440px !important;
  margin: auto;
}

/* Fix for the images on small devices in Safari */
.v-responsive__content {
  width: unset !important;
}

/* a lot of the styles here have to be moved to a dedicated file with global styles */
/* + sass variables have to be introduced */
.v-btn {
  border-width: 2px !important;
  font-family: "Source Sans 3", sans-serif !important;
  font-size: 20px !important;
  font-weight: 400 !important;
  letter-spacing: 0 !important;
  text-indent: 0 !important;
}

.v-divider {
  border-color: #757575;
}

@media only screen and (min-width: 1264px) {
  .v-main .v-container:not(.full-width), footer .v-container{
    max-width: 1185px !important;
  }
}

a {
  color: #57509c;
}

* {
  text-transform: none !important;
}

.v-carousel__controls {
  background-color: white !important;
}

iframe {
  max-width: 100%;
}

/* fade animation */

.fade-enter {
  opacity: 0;
}

.fade-enter-active {
  transition: opacity 0.4s;
}

.fade-leave {
  opacity: 1;
}

.fade-leave-active {
  transition: opacity 0.4s;
  opacity: 0;
}

/* end of fade animation */

.square-left {
  width: 50px;
  height: 7px;
  background-color: #91D1DE;
  border-color: #91D1DE;
}

.square-right {
  width: 50px;
  height: 7px;
  margin-right: 0;
  margin-left: auto;
}

.v-input__slot,
.theme--light.v-text-field--solo .v-input__slot {
  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  border-radius: 5px;
}

.small {
  font-size: 16px;
}

.semi-small {
  font-size: 17px;
}

.font-nineteen {
  font-size: 19px;
}

/* dialogs / cards */
.v-dialog>.v-card>.v-card__title {
  /*https://github.com/vuetifyjs/vuetify/issues/9130*/
  word-break: normal!important;
  font-size: 30px!important;
}
.v-dialog>.v-card>.v-card__text, .v-dialog>.v-card .v-btn {
  font-size: inherit!important;
  line-height: inherit;
}
.open-zammad-chat {
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 10px 20px;
  background-color: #57509c;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  z-index: 1000;
}
.v-data-table-header__content {
  font-size: 21px;
  font-weight: bolder
}

td, th {
  font-size: 21px;
}

.v-expansion-panel-title {
  font-size: 22px!important;
}
</style>
